import { Button, Checkbox, Drawer, Text } from "@medusajs/ui"
import { useMemo, useState } from "react"
import { countryName, countryOptions, EU_COUNTRIES, EUROPE_NON_EU } from "@/lib/geo"
import { SearchInput } from "./ui"

/** Regional-indicator flag emoji for an ISO code (decorative). */
export const flag = (code: string) => String.fromCodePoint(...code.toUpperCase().split("").map((c) => 127397 + c.charCodeAt(0)))

/**
 * Shopify-style "Add countries/regions" drawer: search, tick, group shortcuts.
 * `taken` maps a country (lowercase) to where it already belongs ("Europe market"):
 * those rows are shown but can't be ticked.
 */
export function CountryPicker({
  title = "Countries/regions",
  selected,
  taken = {},
  only,
  onDone,
  onClose,
}: {
  title?: string
  selected: string[]
  taken?: Record<string, string>
  /** Limit the list to these countries (e.g. the store's markets). */
  only?: string[]
  onDone: (codes: string[]) => void
  onClose: () => void
}) {
  const [q, setQ] = useState("")
  const [picked, setPicked] = useState(() => new Set(selected.map((c) => c.toLowerCase())))
  const all = useMemo(() => countryOptions(only), [only])
  const free = useMemo(() => all.filter((c) => !taken[c.code]).map((c) => c.code), [all, taken])
  const shown = useMemo(() => {
    const s = q.trim().toLowerCase()
    return s ? all.filter((c) => c.name.toLowerCase().includes(s) || c.code === s) : all
  }, [all, q])
  const toggle = (code: string, on: boolean) =>
    setPicked((p) => {
      const n = new Set(p)
      on ? n.add(code) : n.delete(code)
      return n
    })
  const group = (codes: string[]) => {
    const avail = codes.map((c) => c.toLowerCase()).filter((c) => free.includes(c))
    const allOn = avail.length > 0 && avail.every((c) => picked.has(c))
    setPicked((p) => {
      const n = new Set(p)
      avail.forEach((c) => (allOn ? n.delete(c) : n.add(c)))
      return n
    })
  }
  const shortcuts: [string, string[]][] = [
    ["European Union", EU_COUNTRIES],
    ["Rest of Europe", EUROPE_NON_EU],
    ["Everywhere else", free.map((c) => c.toUpperCase())],
  ]
  return (
    <Drawer open onOpenChange={(o) => !o && onClose()}>
      <Drawer.Content>
        <Drawer.Header>
          <Drawer.Title>{title}</Drawer.Title>
        </Drawer.Header>
        <Drawer.Body className="flex flex-col gap-y-3 overflow-y-auto">
          <SearchInput value={q} onChange={setQ} placeholder="Search countries/regions" />
          {!only && !q && (
            <div className="flex flex-wrap gap-2">
              {shortcuts.map(([label, codes]) => (
                <Button key={label} type="button" size="small" variant="secondary" onClick={() => group(codes)}>
                  {label}
                </Button>
              ))}
            </div>
          )}
          <ul className="divide-y rounded-lg border" aria-label="Countries/regions">
            {shown.map((c) => {
              const owner = taken[c.code]
              return (
                <li key={c.code}>
                  <label className={"flex items-center gap-x-3 px-3 py-2 " + (owner ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:bg-ui-bg-base-hover")}>
                    <Checkbox disabled={!!owner} checked={picked.has(c.code)} onCheckedChange={(v) => toggle(c.code, v === true)} />
                    <span aria-hidden className="text-[16px] leading-none">{flag(c.code)}</span>
                    <Text size="small" className="flex-1 truncate">{c.name}</Text>
                    {owner && <Text size="xsmall" className="text-ui-fg-muted">In {owner}</Text>}
                  </label>
                </li>
              )
            })}
            {!shown.length && <li className="px-3 py-6 text-center text-[13px] text-ui-fg-muted">No countries/regions found</li>}
          </ul>
        </Drawer.Body>
        <Drawer.Footer>
          <Text size="small" className="mr-auto text-ui-fg-subtle">{picked.size} selected</Text>
          <Button type="button" variant="secondary" size="small" onClick={onClose}>Cancel</Button>
          <Button type="button" size="small" onClick={() => (onDone([...picked].sort((a, b) => countryName(a).localeCompare(countryName(b)))), onClose())}>
            Done
          </Button>
        </Drawer.Footer>
      </Drawer.Content>
    </Drawer>
  )
}

/** Compact list of countries with flags, for cards. */
export function CountryList({ codes, max = 8 }: { codes: string[]; max?: number }) {
  const [open, setOpen] = useState(false)
  const sorted = [...codes].sort((a, b) => countryName(a).localeCompare(countryName(b)))
  const list = open ? sorted : sorted.slice(0, max)
  if (!codes.length) return <Text size="small" className="text-ui-fg-muted">No countries/regions</Text>
  return (
    <div className="flex flex-wrap gap-1.5">
      {list.map((c) => (
        <span key={c} className="inline-flex items-center gap-x-1 rounded-md bg-ui-bg-component px-2 py-0.5 text-[12px]">
          <span aria-hidden>{flag(c)}</span>
          {countryName(c)}
        </span>
      ))}
      {sorted.length > max && (
        <button type="button" className="text-[12px] font-medium text-ui-fg-interactive" onClick={() => setOpen(!open)}>
          {open ? "Show less" : `+${sorted.length - max} more`}
        </button>
      )}
    </div>
  )
}
